import React, { useState, useEffect, useRef } from "react"
import { Row, Col, Input, Label, FormGroup } from "reactstrap"
import { FIELD_ORDER, FIELD_LABEL } from "./chequeFieldCatalogue"

// pt -> px at the current mm-per-px `scale`: convert points to millimetres
// (1pt = 25.4/72 mm), then scale that physical size to screen pixels. Do NOT
// use the 96/72 "pt to px at 96 DPI" ratio here — `scale` is already a
// px-per-mm factor, so multiplying by 96/72 on top of it double-counts the
// DPI conversion and renders text several times too large.
const ptToPx = (pt, scale) => pt * (25.4 / 72) * scale

// 1mm at 96 CSS DPI (the standard px-per-inch every browser uses for CSS
// pixels) — editing at this fixed scale shows the cheque at its real
// physical size on screen instead of stretched to fill the column.
const MM_TO_PX_96DPI = 96 / 25.4

// Fixed sample content shown while positioning fields, so the template
// editor looks like a filled cheque instead of empty dashed boxes — matches
// the FRS's own reference test case (58,67,937 -> the words below).
const SAMPLE = {
  date: "06032024",
  payee: "** Demo Cheque Pvt. Ltd. **",
  words1: "** Fifty Eight Lakh Sixty Seven Thousand Nine Hundred Thirty Seven",
  words2: "Only /-",
  amount: "** 58,67,937 /-",
  crossing: "A/C PAYEE",
}

/*
 * Hand-rolled drag-position editor. No canvas/drag library is used anywhere
 * in this codebase (confirmed), so fields are dragged via native pointer
 * events on absolutely-positioned boxes over the cheque leaf image; exact
 * placement is set via the numeric mm inputs on the right. This mirrors the
 * reference prototype's bindDrag()/renderEdProps() split.
 */
const FieldPositionEditor = ({ imageUrl, leaf, fields, onFieldsChange, readOnly = false }) => {
  const [selectedKey, setSelectedKey] = useState("payee")
  const scale = MM_TO_PX_96DPI

  const stateRef = useRef({ fields, selectedKey, leaf })
  stateRef.current = { fields, selectedKey, leaf }

  useEffect(() => {
    const handleKeyDown = event => {
      const isArrowKey = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)
      if (!isArrowKey) return

      const activeTag = document.activeElement?.tagName
      if (activeTag === "INPUT" || activeTag === "TEXTAREA" || activeTag === "SELECT" || document.activeElement?.isContentEditable) {
        return
      }

      const { fields: curFields, selectedKey: curKey, leaf: curLeaf } = stateRef.current
      const field = curFields.find(f => f.key === curKey)
      if (!field || !field.enabled) return

      event.preventDefault()

      const step = event.shiftKey ? 2 : (event.altKey || event.ctrlKey ? 0.1 : 0.5)
      const maxX = Math.max(0, curLeaf.widthMm - field.wMm)
      const maxY = Math.max(0, curLeaf.heightMm - field.hMm)

      let nextX = field.xMm
      let nextY = field.yMm

      if (event.key === "ArrowUp") nextY -= step
      if (event.key === "ArrowDown") nextY += step
      if (event.key === "ArrowLeft") nextX -= step
      if (event.key === "ArrowRight") nextX += step

      nextX = Math.round(Math.min(maxX, Math.max(0, nextX)) * 100) / 100
      nextY = Math.round(Math.min(maxY, Math.max(0, nextY)) * 100) / 100

      onFieldsChange(curFields.map(f => (f.key === curKey ? { ...f, xMm: nextX, yMm: nextY } : f)))
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onFieldsChange])

  const fieldByKey = key => fields.find(f => f.key === key)
  const selectedField = fieldByKey(selectedKey)

  const updateField = (key, patch) => {
    onFieldsChange(fields.map(f => (f.key === key ? { ...f, ...patch } : f)))
  }

  const handlePointerDown = (event, key) => {
    event.preventDefault()
    setSelectedKey(key)

    const field = fieldByKey(key)
    const startX = event.clientX
    const startY = event.clientY
    const originX = field.xMm
    const originY = field.yMm
    const target = event.currentTarget
    target.setPointerCapture(event.pointerId)

    const onMove = moveEvent => {
      const deltaXMm = (moveEvent.clientX - startX) / scale
      const deltaYMm = (moveEvent.clientY - startY) / scale
      const snap = v => Math.round(v * 2) / 2
      // Clamp to the leaf's own width/height so a field can never be dragged
      // past the cheque's edges on any side — previously only the top-left
      // (>= 0) was enforced, so the right/bottom edge allowed unlimited drag.
      const maxX = Math.max(0, leaf.widthMm - field.wMm)
      const maxY = Math.max(0, leaf.heightMm - field.hMm)
      updateField(key, {
        xMm: Math.min(maxX, Math.max(0, snap(originX + deltaXMm))),
        yMm: Math.min(maxY, Math.max(0, snap(originY + deltaYMm))),
      })
    }
    const onUp = () => {
      target.removeEventListener("pointermove", onMove)
      target.removeEventListener("pointerup", onUp)
    }
    target.addEventListener("pointermove", onMove)
    target.addEventListener("pointerup", onUp)
  }

  const MIN_SIZE_MM = 2

  // Bottom-right corner drag handle on the selected field resizes it
  // (wMm/hMm) directly on the cheque, same drag mechanics as move — clamped
  // so the field can never shrink below a usable size or grow past the
  // leaf's own edges.
  const handleResizePointerDown = (event, key) => {
    event.preventDefault()
    event.stopPropagation()

    const field = fieldByKey(key)
    const startX = event.clientX
    const startY = event.clientY
    const originW = field.wMm
    const originH = field.hMm
    const target = event.currentTarget
    target.setPointerCapture(event.pointerId)

    const onMove = moveEvent => {
      const deltaWMm = (moveEvent.clientX - startX) / scale
      const deltaHMm = (moveEvent.clientY - startY) / scale
      const snap = v => Math.round(v * 2) / 2
      const maxW = Math.max(MIN_SIZE_MM, leaf.widthMm - field.xMm)
      const maxH = Math.max(MIN_SIZE_MM, leaf.heightMm - field.yMm)
      updateField(key, {
        wMm: Math.min(maxW, Math.max(MIN_SIZE_MM, snap(originW + deltaWMm))),
        hMm: Math.min(maxH, Math.max(MIN_SIZE_MM, snap(originH + deltaHMm))),
      })
    }
    const onUp = () => {
      target.removeEventListener("pointermove", onMove)
      target.removeEventListener("pointerup", onUp)
    }
    target.addEventListener("pointermove", onMove)
    target.addEventListener("pointerup", onUp)
  }

  const printableHeightMm = leaf.heightMm - (leaf.micrBandMm || 0)
  const overlapsMicrBand = field => field.enabled && field.yMm + field.hMm > printableHeightMm + 0.01
  const outOfBounds = field =>
    field.enabled && (field.xMm < 0 || field.yMm < 0 || field.xMm + field.wMm > leaf.widthMm + 0.01 || field.yMm + field.hMm > leaf.heightMm + 0.01)

  const numberInput = (label, value, onChange, step = 0.5) => (
    <FormGroup className="mb-2">
      <Label className="form-label mb-1">{label}</Label>
      <Input type="number" step={step} value={value ?? 0} onChange={e => onChange(parseFloat(e.target.value) || 0)} />
    </FormGroup>
  )

  return (
    <Row>
      <Col md={12}>
        <div
          style={{
            position: "relative",
            // Fixed width from the leaf's real mm size at 96 CSS DPI, not a
            // container-stretched percentage — shows the cheque at its true
            // physical size (e.g. 202mm) instead of squeezed/stretched to
            // whatever width the surrounding column happens to have.
            width: leaf.widthMm * scale,
            height: leaf.heightMm * scale,
            maxWidth: "100%",
            background: "#f4f6f9",
            border: "1px solid #dfe3ea",
            borderRadius: 6,
            overflow: "hidden",
            userSelect: "none",
          }}
        >
          {imageUrl && (
            <img
              src={imageUrl}
              alt="Cheque leaf"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "fill" }}
            />
          )}
          {FIELD_ORDER.map(key => {
            const field = fieldByKey(key)
            if (!field || !field.enabled) return null
            const bad = overlapsMicrBand(field) || outOfBounds(field)
            const justify = field.align === "CENTER" ? "center" : field.align === "RIGHT" ? "flex-end" : "flex-start"
            return (
              <div
                key={key}
                onPointerDown={e => handlePointerDown(e, key)}
                style={{
                  position: "absolute",
                  left: field.xMm * scale,
                  top: field.yMm * scale,
                  width: field.wMm * scale,
                  height: field.hMm * scale,
                  outline: !readOnly && selectedKey === key ? "2px solid #1976d2" : bad ? "2px dashed #d32f2f" : "1px dashed rgba(25,118,210,.8)",
                  // Only the selected field gets a filled background; every
                  // other field (including out-of-bounds/MICR-overlap
                  // warnings) stays transparent so the underlying cheque
                  // image is never obscured — a filled background here was
                  // showing up as a stray white/pink patch over the artwork.
                  background: !readOnly && selectedKey === key ? "rgba(25,118,210,.14)" : "transparent",
                  cursor: "move",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: key === "date" ? "flex-start" : justify,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  fontFamily: key === "date" ? "'Roboto Mono', monospace" : "inherit",
                  fontWeight: field.bold ? 700 : 400,
                  fontSize: ptToPx(field.fontSizePt || 11, scale),
                  transform: key === "crossing" ? `rotate(${field.rotationDeg || 0}deg)` : undefined,
                  transformOrigin: "center",
                  borderTop: key === "crossing" ? "1px solid #000" : undefined,
                  borderBottom: key === "crossing" ? "1px solid #000" : undefined,
                }}
              >
                {key === "date"
                  ? [...SAMPLE.date].map((c, i) => (
                      <span
                        key={i}
                        style={{
                          position: "absolute",
                          left: i * (field.pitchMm || 5.6) * scale,
                          width: (field.pitchMm || 5.6) * scale,
                          textAlign: "center",
                        }}
                      >
                        {c}
                      </span>
                    ))
                  : SAMPLE[key]}
                {!readOnly && selectedKey === key && (
                  <div
                    onPointerDown={e => handleResizePointerDown(e, key)}
                    style={{
                      position: "absolute",
                      right: 0,
                      bottom: 0,
                      width: 10,
                      height: 10,
                      background: "#1976d2",
                      border: "1px solid #fff",
                      cursor: "nwse-resize",
                    }}
                  />
                )}
              </div>
            )
          })}
        </div>
        {FIELD_ORDER.some(k => fieldByKey(k) && overlapsMicrBand(fieldByKey(k))) && (
          <div className="alert alert-warning mt-2 py-2 px-3 mb-0" style={{ fontSize: 13 }}>
            One or more fields overlap the MICR band (bottom {leaf.micrBandMm} mm). Move them before saving.
          </div>
        )}
        {FIELD_ORDER.some(k => fieldByKey(k) && outOfBounds(fieldByKey(k))) && (
          <div className="alert alert-danger mt-2 py-2 px-3 mb-0" style={{ fontSize: 13 }}>
            One or more fields are outside the cheque leaf. Move or resize them before saving.
          </div>
        )}
      </Col>

      {!readOnly && (
      <Col md={12} className="mt-3">
        <div className="d-flex flex-wrap gap-2 mb-2">
          {FIELD_ORDER.map(key => {
            const isSelected = selectedKey === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedKey(key)}
                className="btn btn-sm"
                style={{
                  border: "1px solid " + (isSelected ? "#1976d2" : "#dfe3ea"),
                  background: isSelected ? "#e7f1fc" : "#fff",
                  color: isSelected ? "#1976d2" : "#495057",
                }}
              >
                {FIELD_LABEL[key]}
              </button>
            )
          })}
        </div>
        <div className="text-muted mb-3" style={{ fontSize: 12 }}>
          💡 <strong>Keyboard:</strong> Use <kbd style={{ padding: "1px 5px", fontSize: 11, background: "#f1f3f5", border: "1px solid #ced4da", borderRadius: 3, color: "#333" }}>↑</kbd> <kbd style={{ padding: "1px 5px", fontSize: 11, background: "#f1f3f5", border: "1px solid #ced4da", borderRadius: 3, color: "#333" }}>↓</kbd> <kbd style={{ padding: "1px 5px", fontSize: 11, background: "#f1f3f5", border: "1px solid #ced4da", borderRadius: 3, color: "#333" }}>←</kbd> <kbd style={{ padding: "1px 5px", fontSize: 11, background: "#f1f3f5", border: "1px solid #ced4da", borderRadius: 3, color: "#333" }}>→</kbd> to move selected field (hold <kbd style={{ padding: "1px 5px", fontSize: 11, background: "#f1f3f5", border: "1px solid #ced4da", borderRadius: 3, color: "#333" }}>Shift</kbd> for 2mm jump)
        </div>

        {selectedField && (
          <Row>
            <Col xs={4}>{numberInput("X from left (mm)", selectedField.xMm, v => updateField(selectedKey, { xMm: v }))}</Col>
            <Col xs={4}>{numberInput("Y from top (mm)", selectedField.yMm, v => updateField(selectedKey, { yMm: v }))}</Col>
            <Col xs={4}>{numberInput("Width (mm)", selectedField.wMm, v => updateField(selectedKey, { wMm: v }))}</Col>
            <Col xs={4}>{numberInput("Height (mm)", selectedField.hMm, v => updateField(selectedKey, { hMm: v }))}</Col>
            <Col xs={4}>{numberInput("Font size (pt)", selectedField.fontSizePt, v => updateField(selectedKey, { fontSizePt: v }), 0.5)}</Col>
            <Col xs={4}>
              <FormGroup className="mb-2">
                <Label className="form-label mb-1">Align</Label>
                <Input type="select" value={selectedField.align || "LEFT"} onChange={e => updateField(selectedKey, { align: e.target.value })}>
                  <option value="LEFT">Left</option>
                  <option value="CENTER">Center</option>
                  <option value="RIGHT">Right</option>
                </Input>
              </FormGroup>
            </Col>
            {selectedKey === "date" && (
              <Col xs={4}>
                {numberInput("Box pitch (mm per digit)", selectedField.pitchMm ?? 5.6, v => updateField(selectedKey, { pitchMm: v }), 0.05)}
              </Col>
            )}
            {selectedKey === "crossing" && (
              <Col xs={4}>
                {numberInput("Rotation (degrees)", selectedField.rotationDeg ?? 0, v => updateField(selectedKey, { rotationDeg: v }), 1)}
              </Col>
            )}
            <Col xs={4} className="d-flex align-items-center gap-2">
              <button
                type="button"
                onClick={() => updateField(selectedKey, { bold: !(selectedField.bold === true) })}
                style={{
                  width: 18,
                  height: 18,
                  padding: 0,
                  flex: "none",
                  border: "1px solid " + (selectedField.bold ? "#1976d2" : "#adb5bd"),
                  borderRadius: 3,
                  background: selectedField.bold ? "#1976d2" : "#fff",
                  color: "#fff",
                  fontSize: 12,
                  lineHeight: "16px",
                  cursor: "pointer",
                }}
                aria-label="Toggle bold"
              >
                {selectedField.bold ? "✓" : ""}
              </button>
              <span style={{ cursor: "pointer" }} onClick={() => updateField(selectedKey, { bold: !(selectedField.bold === true) })}>
                Bold
              </span>
            </Col>
            {/* No show/hide toggle for crossing here — a template always
                positions crossing enabled; whether a given printed cheque
                actually shows it is a print-time choice, not a
                template-editing one. */}
          </Row>
        )}
      </Col>
      )}
    </Row>
  )
}

export default FieldPositionEditor
